import type { GrammarError, AIComment } from '$lib/types/grammar';
import type { AIClient } from '$lib/types/ai';
import { AIUtilityService } from './AIUtilityService';

/**
 * AI Error Analysis Service
 * Handles individual grammar error analysis and AI commenting
 * Single Responsibility: Individual error analysis and commenting
 */
export class AIErrorAnalysisService {
  /**
   * Analyze a single grammar error and generate AI comment
   */
  static async analyzeGrammarError(
    error: GrammarError, 
    contextText: string, 
    aiClient: AIClient,
    useStreaming: boolean = false
  ): Promise<AIComment> {
    // Use the actual error text from the error object, not position-based extraction
    let contextInfo: { before: string; error: string; after: string };
    
    // If we have the original error text, use it directly
    if (error.original) {
      // Find the actual position of the error text in context
      const errorIndex = contextText.indexOf(error.original);
      if (errorIndex !== -1) {
        const contextSize = 100;
        const beforeStart = Math.max(0, errorIndex - contextSize);
        const afterEnd = Math.min(contextText.length, errorIndex + error.original.length + contextSize);
        
        contextInfo = {
          before: contextText.substring(beforeStart, errorIndex),
          error: error.original,  // Use the actual error text
          after: contextText.substring(errorIndex + error.original.length, afterEnd)
        };
      } else {
        // Fallback to position-based extraction if text not found
        contextInfo = AIUtilityService.extractErrorContext(
          contextText, 
          error.position.start, 
          error.position.end, 
          100
        );
        // Override with original text if available
        if (error.original) {
          contextInfo.error = error.original;
        }
      }
    } else {
      // Fallback to position-based extraction
      contextInfo = AIUtilityService.extractErrorContext(
        contextText, 
        error.position.start, 
        error.position.end, 
        100
      );
    }

    // Create prompt for analyzing this specific error with full context
    const prompt = this.createErrorAnalysisPrompt(error, contextInfo, contextText, aiClient);

    let response: string;
    
    if (useStreaming && aiClient.supportsStreaming?.()) {
      // For now, use non-streaming as fallback
      // TODO: Implement actual streaming when client supports it
      response = await aiClient.sendMessage(prompt);
    } else {
      response = await aiClient.sendMessage(prompt);
    }

    // Parse AI response and create comment
    return this.parseErrorAnalysisResponse(response, error, aiClient);
  }

  /**
   * Create prompt for analyzing a specific error
   */
  private static createErrorAnalysisPrompt(
    error: GrammarError, 
    context: { before: string; error: string; after: string },
    fullText?: string,
    aiClient?: AIClient
  ): string {
    // Check if this is a free/simple model that needs simpler prompts
    const isSimpleModel = aiClient && (
      aiClient.getModelName().includes('gpt-oss') || 
      aiClient.getModelName().includes('qwen')
    );
    
    if (isSimpleModel) {
      // Simpler prompt for GPT-OSS and Qwen models
      return `문맥: "${context.before}[${context.error}]${context.after}"
오류: "${context.error}"
수정 제안: "${error.suggestions?.[0] || '제안 없음'}"
설명: ${error.help || error.message}

"${context.error}"를 "${error.suggestions?.[0] || '원본 유지'}"로 바꾸는 것이 맞나요?

중요: alternatives에는 "${error.suggestions?.[0] || '제안 없음'}"과 다른 대안만 포함하세요.
"${error.suggestions?.[0] || '제안 없음'}"과 같은 대안은 절대 alternatives에 포함하지 마세요.

JSON으로 답하세요:
{
  "agrees": true/false,
  "comment": "왜 동의하거나 반대하는지 한 문장 설명",
  "alternatives": [],
  "confidence": 0.8
}`;
    }
    
    // Include full text if available and not too long
    const fullTextSection = fullText && fullText.length < 1000 
      ? `\n전체 텍스트:\n"${fullText}"\n` 
      : '';
    
    return `한국어 맞춤법 오류를 분석하고 있습니다.
${fullTextSection}
현재 검토 중인 오류:
- 오류 텍스트: "${context.error}" (이 부분만 분석하세요)
- 위치: "${context.before}[${context.error}]${context.after}"
- 제안된 수정: "${error.suggestions?.[0] || '제안 없음'}"
- 오류 설명: ${error.help || error.message}

중요 지침:
- 오직 "${context.error}" 부분에 대해서만 분석하세요
- 주변 텍스트의 다른 오류는 언급하지 마세요
- "${context.error}"를 "${error.suggestions?.[0] || '원본 유지'}"로 바꾸는 것이 적절한지만 판단하세요

분석 내용:
1. 제안된 수정("${error.suggestions?.[0] || '제안 없음'}")이 "${context.error}" 자리에 적절한지 판단
2. "${context.error}"에 대한 설명만 작성 (다른 부분의 오류는 언급 금지)
3. "${context.error}"에 대한 더 나은 대안이 있다면 제시
4. 확신도 (0-1)

중요: alternatives에는 제안된 수정("${error.suggestions?.[0] || '제안 없음'}")과 다른 대안만 포함해야 합니다.
제안된 수정과 같은 대안은 alternatives에 포함하지 마세요.

JSON 형식으로 응답:
{
  "agrees": boolean,
  "comment": "${context.error}에 대한 설명만 (다른 오류 언급 금지)",
  "alternatives": ["${context.error}를 대체할 대안들 (제안된 수정과 다른 것만)"],
  "confidence": number
}`;
  }

  /**
   * Parse AI response and create AIComment
   */
  private static parseErrorAnalysisResponse(response: string, error: GrammarError, aiClient: AIClient): AIComment {
    let parsedResponse;
    
    // Debug log for GPT-OSS
    if (aiClient.getModelName().includes('gpt-oss') || aiClient.getModelName().includes('qwen')) {
    }
    
    try {
      const cleanResponse = AIUtilityService.cleanJsonResponse(response);
      parsedResponse = JSON.parse(cleanResponse);
    } catch (e) {
      // Try to extract JSON from response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          parsedResponse = JSON.parse(jsonMatch[0]);
        } catch (e2) {
          // If all parsing fails, provide a default response
          parsedResponse = {
            agrees: true,
            comment: '오류를 분석했습니다.',
            alternatives: [],
            confidence: 0.7
          };
        }
      } else {
        // Fallback if response is not valid JSON
        parsedResponse = {
          agrees: true,
          comment: '오류를 분석했습니다.',
          alternatives: [],
          confidence: 0.7
        };
      }
    }

    // Ensure comment is a string, not an object
    let commentText = parsedResponse.comment;
    if (typeof commentText === 'object') {
      commentText = JSON.stringify(commentText);
    }
    if (!commentText || typeof commentText !== 'string') {
      commentText = '오류를 분석했습니다.';
    }
    
    // Filter out alternatives that are the same as the original or the suggested corrections
    const alternatives = (parsedResponse.alternatives || [])
      .filter((alt: string) => {
        // Trim and normalize the alternative
        const normalizedAlt = alt.trim();
        
        // Remove alternatives that match the original text
        if (normalizedAlt === error.original) {
          return false;
        }
        
        // Remove alternatives that match any of the suggested corrections
        if (error.suggestions?.some(suggestion => suggestion.trim() === normalizedAlt)) {
          return false;
        }
        
        // Case-insensitive check for duplicates
        const lowerAlt = normalizedAlt.toLowerCase();
        if (error.suggestions?.some(suggestion => suggestion.trim().toLowerCase() === lowerAlt)) {
          return false;
        }
        
        return true;
      });
    
    // Final alternatives determined
    
    // Determine if AI suggests keeping original
    const suggestsKeepOriginal = !parsedResponse.agrees && (
      // No alternatives provided
      alternatives.length === 0 ||
      // Alternative is the same as original
      (alternatives.length === 1 && alternatives[0] === error.original) ||
      // Comment explicitly mentions keeping original
      commentText.includes('원본') && (commentText.includes('유지') || commentText.includes('그대로')) ||
      commentText.includes('문제없') ||
      commentText.includes('수정할 필요 없') ||
      commentText.includes('올바른 표현')
    );
    
    const aiComment: AIComment = {
      text: commentText,
      confidence: parsedResponse.confidence || 0.7,
      agreesWithCorrection: parsedResponse.agrees === true,
      alternatives,
      isUncertain: parsedResponse.confidence < 0.6,
      suggestsKeepOriginal,
      timestamp: Date.now(),
      model: aiClient.getModelName() || 'unknown'
    };

    return aiComment;
  }

  /**
   * Batch analyze multiple errors
   */
  static async batchAnalyzeErrors(
    errors: GrammarError[],
    contextText: string,
    aiClient: AIClient,
    useStreaming: boolean = false,
    onProgress?: (completed: number, total: number) => void
  ): Promise<Map<string, AIComment>> {
    const comments = new Map<string, AIComment>();
    
    for (let i = 0; i < errors.length; i++) {
      const error = errors[i];
      
      try {
        const comment = await this.analyzeGrammarError(
          error, 
          contextText,  // Now passing full context text
          aiClient, 
          useStreaming
        );
        
        comments.set(error.id, comment);
        
        // Report progress if callback provided
        if (onProgress) {
          onProgress(i + 1, errors.length);
        }
      } catch (analysisError) {
        console.warn(`Failed to analyze error ${errors[i].id}:`, analysisError);
        
        // Create a fallback comment
        const fallbackComment: AIComment = {
          text: '분석 중 오류가 발생했습니다.',
          confidence: 0.1,
          agreesWithCorrection: false,
          alternatives: [],
          isUncertain: true,
          suggestsKeepOriginal: false,
          timestamp: Date.now(),
          model: aiClient.getModelName() || 'unknown'
        };
        
        comments.set(errors[i].id, fallbackComment);
        
        if (onProgress) {
          onProgress(i + 1, errors.length);
        }
      }
    }
    
    return comments;
  }

  /**
   * Create a simple AI comment for cases where full analysis fails
   */
  static createFallbackComment(aiClient: AIClient): AIComment {
    return {
      text: '오류를 분석했습니다.',
      confidence: 0.7,
      agreesWithCorrection: true,
      alternatives: [],
      isUncertain: false,
      suggestsKeepOriginal: false,
      timestamp: Date.now(),
      model: aiClient.getModelName() || 'unknown'
    };
  }

  /**
   * Validate and sanitize comment text
   */
  static sanitizeCommentText(text: any): string {
    if (typeof text === 'object') {
      return JSON.stringify(text);
    }
    if (!text || typeof text !== 'string') {
      return '오류를 분석했습니다.';
    }
    return text;
  }

  /**
   * Filter valid alternatives
   */
  static filterAlternatives(alternatives: any[], originalText: string): string[] {
    if (!Array.isArray(alternatives)) {
      return [];
    }
    
    return alternatives
      .filter((alt): alt is string => typeof alt === 'string')
      .filter(alt => alt !== originalText && alt.trim().length > 0);
  }
}